import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import './Booking.css';
import './ServiceCard.css';
import { apiRequest } from './utils';
import translations from './lang';

const Booking = () => {
  const { userId, serviceId } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [services, setServices] = useState([]);
  const [rawAppointments, setRawAppointments] = useState([]); // ← nyers adat
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentService, setCurrentService] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [timeSlot, setTimeSlot] = useState('01:00');
  const [expandedServiceId, setExpandedServiceId] = useState(null);
  const calendarRef = useRef(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const language = localStorage.getItem('language') || 'ro';
  const t = translations[language];
  const [appointmentPackages, setAppointmentPackages] = useState([]);

  // Google Maps modál
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapAddress, setMapAddress] = useState('');
  const openMapModal = (addr) => {
    if (!addr) return;
    setMapAddress(addr);
    setMapModalOpen(true);
  };

  // Szabadságok az adott szolgáltatóhoz
  const [vacations, setVacations] = useState([]);

  // Dátum formázó
  const fmtDMY = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  // Releváns szabadság adott service-hez
  const pickRelevantVacation = (service) => {
    if (!Array.isArray(vacations) || vacations.length === 0) return null;
    const now = new Date();
    const relevant = vacations.filter(
      (v) => v && (v.service === null || v.service === service.id)
    );
    if (relevant.length === 0) return null;

    const active = relevant.find((v) => {
      const s = new Date(v.start_at);
      const e = new Date(v.end_at);
      return s <= now && now <= e;
    });
    if (active) return active;

    const future = relevant
      .filter((v) => new Date(v.start_at) > now)
      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    return future[0] || null;
  };

  // Bejelentkezett user ID JWT-ből
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    let decodedUserId = null;

    if (token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      decodedUserId = payload.user_id;
    }

    setCurrentUserId(decodedUserId);
  }, []);

  // Profil + szolgáltatások
  useEffect(() => {
    if (!userId) return;

    apiRequest(`${process.env.REACT_APP_API}/api/user-profile/${userId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'Accept-Language': language,
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error('A szerver hibát adott vissza.');
        return response.json();
      })
      .then((data) => setProfileData(data))
      .catch((error) => {
        console.error('Hiba az adatok lekérésekor:', error);
        setError('Hiba: A szerver hibát adott vissza.');
      })
      .finally(() => setLoading(false));

    fetch(`${process.env.REACT_APP_API}/api/user-services-by-user-id/${userId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'Accept-Language': language,
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to fetch services');
        return response.json();
      })
      .then((data) => {
        const list = Array.isArray(data?.data) ? data.data : [];
        setServices(list);
        if (!serviceId && list.length > 0) {
          setCurrentService(list[0].id);
          setTimeSlot(list[0].interval);
          fetchAppointments(list[0].id);
        } else if (serviceId) {
          fetchAppointments(serviceId);
        }
      })
      .catch((error) => {
        console.error('Error fetching services:', error);
      });
  }, [userId, serviceId, language]);

  // Szabadságok
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API}/api/vacations-by-user-id/${userId}/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Accept-Language': language,
          },
        });
        const data = await res.json().catch(() => ({ data: [] }));
        setVacations(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        console.error('Vacations fetch error:', e);
        setVacations([]);
      }
    })();
  }, [userId, language]);

  // Csomagok betöltése modálhoz
  useEffect(() => {
    const loadAppointmentPackages = async () => {
      if (!showPackageModal || !selectedAppointment?.id) return;

      try {
        const res = await apiRequest(
          `${process.env.REACT_APP_API}/api/appointment-packages/${selectedAppointment.id}/`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
              'Accept-Language': localStorage.getItem('language') || 'ro',
            },
          }
        );
        const data = await res.json();
        setAppointmentPackages(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Packages fetch error:', e);
        setAppointmentPackages([]);
      }
    };

    loadAppointmentPackages();
  }, [showPackageModal, selectedAppointment]);

  // Időpontok lekérése (nyers)
  const fetchAppointments = (svcId) => {
    const sid = typeof svcId === 'string' ? parseInt(svcId, 10) : svcId;
    if (!sid) return;

    fetch(`${process.env.REACT_APP_API}/api/appointments-by-service-id/${sid}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'Accept-Language': language,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        const list = Array.isArray(data?.data) ? data.data : [];
        setRawAppointments(list);
      })
      .catch((error) => {
        console.error('Error fetching appointments:', error);
      });
  };

  // Mindig a legfrissebb currentUserId-vel formázunk
  const appointments = useMemo(() => {
    return rawAppointments.map((event) => {
      const bookings = Array.isArray(event.bookings) ? event.bookings : [];
      const bookedCount = bookings.length;
      const maxCapacity = event.max_capacity || 1;

      const userBooking = bookings.find((b) => b.user === currentUserId);

      let statusSymbol = '';
      if (userBooking && typeof userBooking.status === 'string') {
        const s = userBooking.status.toLowerCase();
        if (s === 'confirmed') statusSymbol = ' ✅';
        else if (s === 'rejected') statusSymbol = ' ❌';
        else if (s === 'pending') statusSymbol = ' ⚠️';
      }

      return {
        id: event.id,
        title: `${event.service_name}-${bookedCount}/${maxCapacity}${statusSymbol}`,
        start: new Date(event.start_time),
        end: new Date(event.end_time),
        bookings,
        color: bookedCount === maxCapacity ? 'red' : bookedCount > 0 ? 'orange' : '',
      };
    });
  }, [rawAppointments, currentUserId]);

  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const appointmentId = event.id;

    const selService = services.find(
      (service) => service.id === (currentService || parseInt(serviceId, 10))
    );
    setSelectedService(selService || null);
    setSelectedAppointment({ id: appointmentId, title: event.title });
    setSelectedEvent(event);
    setShowPackageModal(true);
  };

  // Naptár fókusz segédfüggvény
  const focusCalendar = (opts = { scroll: true }) => {
    const el = calendarRef.current;
    if (!el) return;
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    if (opts.scroll) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.focus({ preventScroll: !opts.scroll });
  };

  const handleServiceSelection = (svcId) => {
    setCurrentService(svcId);
    fetchAppointments(svcId);
    focusCalendar(); // görget + fókusz a naptárra
  };

  const handleIntervalChange = (e) => setTimeSlot(e.target.value);

  const calendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    locales: [huLocale], // legalább a HU lokált betöltjük
    initialView: 'timeGridWeek',
    headerToolbar: { left: 'prev,next today', center: 'title', right: 'timeGridWeek,timeGridDay' },
    views: { timeGridWeek: { dayHeaderFormat: { weekday: 'short', day: 'numeric' } } },
    events: appointments,
    selectable: false,
    selectMirror: false,
    dayMaxEvents: true,
    slotDuration: timeSlot,
    slotLabelInterval: timeSlot,
    slotMinTime: '06:00:00',
    slotMaxTime: '24:00:00',
    allDaySlot: false,
    locale: language, // 'hu' esetén lesz hatása a fent betöltött locale-lal
    buttonText: { today: t.today, month: t.month, week: t.week, day: t.day },
    firstDay: 1,
    height: 'auto',
    eventClick: handleEventClick,
    eventClassNames: function (arg) {
      const bookings = arg.event.extendedProps?.bookings || [];
      if (bookings.some((booking) => booking.user === currentUserId)) {
        return ['booked-event', 'user-booked'];
      }
      return [];
    },
  };

  // Oldal betöltés után fókusz a naptárra (görgetéssel)
  useEffect(() => {
    if (!loading) {
      requestAnimationFrame(() => focusCalendar());
    }
  }, [loading]);

  // Amikor megérkeznek az események, csak fókusz (gördítés nélkül)
  useEffect(() => {
    if (!loading && appointments.length > 0) {
      requestAnimationFrame(() => focusCalendar({ scroll: false }));
    }
  }, [appointments, loading]);

  if (loading) return <div>{t.loading_data}</div>;
  if (error) return <div>{error}</div>;

  const toggleDetails = (id) => setExpandedServiceId(expandedServiceId === id ? null : id);

  const handlePackageSelect = async (appointmentId, packageId) => {
    try {
      const res = await apiRequest(`${process.env.REACT_APP_API}/api/appointments/${appointmentId}/book/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Accept-Language': language,
        },
        body: JSON.stringify({ package_id: packageId }),
      });

      const data = await res.json();

      if (res.ok && data?.success) {
        await fetchAppointments(currentService || parseInt(serviceId, 10));
        if (selectedAppointment?.id) {
          const pkRes = await apiRequest(
            `${process.env.REACT_APP_API}/api/appointment-packages/${selectedAppointment.id}/`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                'Accept-Language': language,
              },
            }
          );
          const pkData = await pkRes.json();
          setAppointmentPackages(Array.isArray(pkData) ? pkData : []);
        }
        alert(t.booking_success);
      } else {
        alert(data?.error || t.booking_failed);
      }
    } catch (err) {
      console.error('Hiba a foglalás során:', err);
      alert(t.booking_error);
    }
  };

  async function handlePackageRemove(packageId) {
    const ok = window.confirm(t.confirm_delete_package);
    if (!ok) return;

    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_API}/api/appointment-packages/${packageId}/delete/`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Accept-Language': language,
          },
        }
      );

      let data = {};
      try {
        data = await res.json();
      } catch (_) {}

      if (res.ok && (data?.success ?? true)) {
        alert(t.package_deleted_success);

        // ✅ 1) csomaglista frissítése (maradhat)
        if (selectedAppointment?.id) {
          const pkRes = await apiRequest(
            `${process.env.REACT_APP_API}/api/appointment-packages/${selectedAppointment.id}/`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                'Accept-Language': language,
              },
            }
          );
          const pkData = await pkRes.json();
          setAppointmentPackages(Array.isArray(pkData) ? pkData : []);
        }

        // ✅ 2) NAPTÁR / EVENTS frissítése (EZ HIÁNYZOTT!)
        await fetchAppointments(currentService || parseInt(serviceId, 10));

        // ✅ opcionális: ha a backend visszaad booked/free-t, itt ki is írhatod/logolhatod
        // console.log('updated minutes:', data.booked_minutes, data.free_minutes);
      } else {
        alert(data?.error || t.package_deleted_error);
      }
    } catch (err) {
      console.error('Hiba a csomag törlésekor:', err);
      alert(t.package_deleted_error);
    }
  }


  async function handleDeleteBooking(appointmentId) {
    const confirmDelete = window.confirm(t.confirm_cancel_booking);
    if (!confirmDelete) return;

    try {
      const res = await apiRequest(`${process.env.REACT_APP_API}/api/appointments/${appointmentId}/unbook/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Accept-Language': language,
        },
        body: JSON.stringify({ booked_by: currentUserId }),
      });

      const data = (await res.json?.()) ?? {};

      if (res.ok && (data.success ?? true)) {
        alert(t.booking_cancelled_success);
        await fetchAppointments(currentService || parseInt(serviceId, 10));
        setSelectedAppointment(null);
        setShowPackageModal(false);
      } else {
        if (res.status === 403) alert(t.not_booked_by_you);
        else alert(data.error || t.booking_cancelled_error);
      }
    } catch (e) {
      console.error('Hiba a foglalás törlése során:', e);
      alert(t.booking_cancelled_error);
    }
  }

  return (
    <div className="booking-container">
      {profileData && (
      <div className="profile-container2">
        <div className="profile-details2">
          <p>
            <strong>{t.name || 'Név'}:</strong>{' '}
            {profileData.user_data.first_name} {profileData.user_data.last_name}
          </p>
        </div>
      </div>
    )}

      <hr />

      <div className="services-container">
        <h3>{t.services}</h3>
        <div className="service-card-grid">
          {services.map((service) => {
            const vac = pickRelevantVacation(service);
            const showVac = Boolean(vac);
            const vacText = showVac
              ? `${t.on_vacation || 'Szabadság'}: ${fmtDMY(vac.start_at)} – ${fmtDMY(vac.end_at)}`
              : '';

            return (
              <div key={service.id} className={`service-card ${expandedServiceId === service.id ? 'expanded' : ''}`}>
                <div
                  className="service-card-background"
                  style={{
                    backgroundImage: service.service_card_picture
                      ? `url(data:image/jpeg;base64,${service.service_card_picture})`
                      : `url(${process.env.REACT_APP_API}/media/profile_pictures/default.jpg)`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    height: '400px',
                    maxHeight: '400px',
                    maxWidth: '400px',
                    borderRadius: '7px',
                    marginBottom: '7px',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Szabadság-badge */}
                  {showVac && (
                    <div
                      className="vacation-badge"
                      style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 2,
                        background: 'rgba(220, 38, 38, 0.92)',
                        color: '#fff',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontWeight: 800,
                        fontSize: '14px',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
                        backdropFilter: 'blur(1px)',
                        letterSpacing: '0.2px',
                      }}
                      title={vacText}
                      aria-label={vacText}
                    >
                      {vacText}
                    </div>
                  )}

                  <div className="service-card-content">
                    <h4>{service.name}</h4>
                    <p>{service.description}</p>
                  </div>
                </div>

                {expandedServiceId === service.id && (
                  <div className="service-card-details">
                    <p>{service.description}</p>

                    {/* Kattintható cím → Google Maps modál */}
                    {service.address ? (
                      <p>
                        {t.address || 'Cím'}:{' '}
                        <button
                          type="button"
                          onClick={() => openMapModal(service.address)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0ea5e9',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            padding: 0,
                            font: 'inherit',
                          }}
                          aria-label="Cím megnyitása térképen"
                          title={t.address || 'Cím'}
                        >
                          {service.address}
                        </button>
                      </p>
                    ) : null}

                    <p>{t.category}:{service.category_name}</p>
                    <p>{t.available}: {service.available ? t.yes : t.no}</p>
                    <p>{t.sms_notification}:{service.SMS_notification ? t.yes : t.no}</p>
                    <p>{t.email_notification}:{service.Email_notification ? t.yes : t.no}</p>

                    <div className="service-card-user-details">
                      <p><strong>{t.packages}:</strong></p>
                      {service.packages.length > 0 ? (
                        service.packages.map((pkg) => (
                          <div key={pkg.id} className="package-details">
                            <p><strong>{t.name}:</strong> {pkg.name}</p>
                            <p><strong>{t.description}:</strong> {pkg.description}</p>
                            <p><strong>{t.price}:</strong> {pkg.price} RON</p>
                            <p>
                              <strong>{t.duration}:</strong> {parseInt(pkg.duration)} {t.hours}{' '}
                              {Math.round((pkg.duration % 1) * 60)} {t.minutes}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p>{t.no_packages_for_service}</p>
                      )}
                    </div>
                  </div>
                )}
                <div className="service-card-button-container">
                  <button onClick={() => toggleDetails(service.id)}>
                    {expandedServiceId === service.id ? t.close : t.details}
                  </button>
                  <button onClick={() => handleServiceSelection(service.id)}>{t.view}</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <hr />

      {/* Fókuszolható naptár wrapper */}
      <div
        ref={calendarRef}
        tabIndex={-1}
        role="region"
        aria-label={t.calendar || 'Naptár'}
      >
        <FullCalendar {...calendarOptions} />
      </div>

      {showPackageModal && (
        <div className="modal">
          <div className="modal-content">
            <button className="close-icon" onClick={() => setShowPackageModal(false)}>×</button>
            <h4>{t.select_package}</h4>

            {appointmentPackages?.length > 0 ? (
              <table className="package-table">
                <thead>
                  <tr>
                    <th>{t.name}</th>
                    <th>{t.description}</th>
                    <th>{t.price}</th>
                    <th>{t.duration}</th>
                    <th>{t.action}</th>
                  </tr>
                </thead>

                <tbody>
                  {appointmentPackages.map((pkg) => (
                    <tr key={pkg.id}>
                      {/* ✅ CSAK EZEK VÁLTOZTAK: data-label */}
                      <td data-label={t.name}>{pkg.service_package_name}</td>
                      <td data-label={t.description}>{pkg.service_package_description}</td>
                      <td data-label={t.price}>{pkg.service_package_price} RON</td>
                      <td data-label={t.duration}>
                        {Math.floor(pkg.service_package_duration)} {t.hours}{' '}
                        {Math.round((pkg.service_package_duration % 1) * 60)} {t.minutes}
                      </td>
                      <td data-label={t.action}>
                        <button onClick={() => handlePackageRemove(pkg.id)}>{t.delete}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>{t.no_packages_for_appointment}</p>
            )}

            {selectedService?.packages?.length > 0 ? (
              selectedService.packages.map((pkg) => (
                <div key={pkg.id} className="package-item">
                  <p><strong>{pkg.name}</strong></p>
                  <p>{pkg.description}</p>
                  <p>{t.price}: {pkg.price} RON</p>
                  <p>
                    {t.duration}: {Math.floor(pkg.duration)} {t.hours}{' '}
                    {Math.round((pkg.duration % 1) * 60)} {t.minutes}
                  </p>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button onClick={() => handlePackageSelect(selectedAppointment.id, pkg.id)}>
                      {t.choose}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>{t.no_packages_for_appointment}</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
              <button onClick={() => handleDeleteBooking(selectedAppointment.id)}>
                {t.delete_booking}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Térkép modál */}
      {mapModalOpen && (
        <div className="modal" role="dialog" aria-modal="true" onClick={() => setMapModalOpen(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: 900, width: '95%', height: '70vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h4 style={{ margin: 0 }}>{t.address || 'Cím'}</h4>
              <button className="close-icon" onClick={() => setMapModalOpen(false)} aria-label="Bezárás">×</button>
            </div>

            <div style={{ margin: '8px 0 12px' }}>{mapAddress}</div>

            <iframe
              title="Google Maps"
              width="100%"
              height="100%"
              src={`https://www.google.com/maps?q=${encodeURIComponent(mapAddress)}&hl=${encodeURIComponent(language)}&z=15&output=embed`}
              loading="lazy"
              style={{ border: 0, borderRadius: 8, width: '100%', height: 'calc(100% - 60px)' }}
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />

            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapAddress)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.open_in_maps || 'Megnyitás a Google Térképen'}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Booking;
